<app-page-breadcrumb pageTitle="Editar perfil"></app-page-breadcrumb>

<div class="max-w-3xl mx-auto">
    <div class="mb-6">
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Editar perfil</h1>
        <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Actualiza tu información personal y preferencias básicas.
        </p>
    </div>

    <form [formGroup]="profileForm" (ngSubmit)="onUpdateProfile()">
        <!-- Foto de perfil -->
        <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-6 mb-6">
            <div class="flex items-center gap-2 mb-5">
                <svg class="w-5 h-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                <h2 class="text-base font-semibold text-gray-900 dark:text-white">Foto de perfil</h2>
            </div>

            <div class="flex flex-col sm:flex-row sm:items-center gap-5">
                <div class="relative shrink-0">
                    @if (avatarUrl) {
                    <img [src]="avatarUrl" alt="Foto de perfil"
                        class="w-20 h-20 rounded-full object-cover border border-gray-200 dark:border-gray-700">
                    } @else {
                    <div
                        class="w-20 h-20 rounded-full bg-brand-50 dark:bg-brand-500/10 border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                        <span class="text-xl font-semibold text-brand-600 dark:text-brand-400">{{ avatarInitials }}</span>
                    </div>
                    }
                    @if (uploadingAvatar) {
                    <div class="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                        <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
                            viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </div>
                    }
                </div>

                <div class="flex-1">
                    <h3 class="font-medium text-gray-900 dark:text-white">Tu foto de perfil</h3>
                    <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Esta imagen se mostrará en tu cuenta y en las interacciones que realices.
                    </p>

                    <div class="flex flex-wrap items-center gap-3 mt-3">
                        <label
                            class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-brand-500 text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-500/10 transition-colors cursor-pointer"
                            [class.opacity-60]="uploadingAvatar" [class.cursor-not-allowed]="uploadingAvatar">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
                            </svg>
                            Cambiar foto
                            <input type="file" class="hidden" accept="image/jpeg,image/png,image/gif,image/webp"
                                [disabled]="uploadingAvatar" (change)="onAvatarSelected($event)">
                        </label>

                        @if (avatarUrl) {
                        <button type="button" (click)="onRemoveAvatar()" [disabled]="uploadingAvatar"
                            class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                            Quitar foto
                        </button>
                        }
                    </div>

                    <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">JPG, PNG, GIF o WebP. Máx. 2 MB.</p>
                </div>
            </div>
        </div>

        <!-- Información personal -->
        <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-6 mb-6">
            <div class="flex items-center gap-2 mb-1">
                <svg class="w-5 h-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
                <h2 class="text-base font-semibold text-gray-900 dark:text-white">Información personal</h2>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-5 ml-7">Actualiza tu información personal.</p>

            <div class="space-y-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label for="fullname" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Nombre <span class="text-error-500">*</span>
                        </label>
                        <input type="text" id="fullname" formControlName="fullname"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:bg-gray-800 dark:text-white transition-colors"
                            [class.border-error-500]="isFieldInvalid('fullname')" placeholder="Ej: Juan">
                        @if (isFieldInvalid('fullname')) {
                        <p class="mt-1.5 text-sm text-error-500">{{ getFieldError('fullname') }}</p>
                        }
                    </div>

                    <div>
                        <label for="lastname" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                            Apellido
                        </label>
                        <input type="text" id="lastname" formControlName="lastname"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:bg-gray-800 dark:text-white transition-colors"
                            [class.border-error-500]="isFieldInvalid('lastname')" placeholder="Ej: Pérez">
                        @if (isFieldInvalid('lastname')) {
                        <p class="mt-1.5 text-sm text-error-500">{{ getFieldError('lastname') }}</p>
                        }
                    </div>
                </div>

                <div>
                    <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        <span class="flex items-center gap-2">
                            Email <span class="text-error-500">*</span>
                            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                            </svg>
                        </span>
                    </label>
                    <input type="email" id="email" formControlName="email"
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                        placeholder="ejemplo@correo.com">
                    <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                        El correo electrónico no puede ser modificado.
                    </p>
                </div>

                <div>
                    <label for="phoneNumber" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Teléfono
                    </label>
                    <div class="flex gap-2">
                        <div class="w-40 shrink-0">
                            <app-country-select [countries]="countries" [value]="phoneCountryCode" placeholder="País"
                                (valueChange)="onPhoneCountryChange($event)" />
                        </div>
                        <input type="tel" id="phoneNumber" formControlName="phoneNumber"
                            class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:bg-gray-800 dark:text-white transition-colors"
                            [class.border-error-500]="isFieldInvalid('phoneNumber')" placeholder="987 654 321">
                    </div>
                    @if (isFieldInvalid('phoneNumber')) {
                    <p class="mt-1.5 text-sm text-error-500">{{ getFieldError('phoneNumber') }}</p>
                    } @else {
                    <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Número de contacto, opcional.</p>
                    }
                </div>

                <div>
                    <label for="jobTitle" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Cargo
                    </label>
                    <input type="text" id="jobTitle" formControlName="jobTitle"
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:bg-gray-800 dark:text-white transition-colors"
                        [class.border-error-500]="isFieldInvalid('jobTitle')" placeholder="Ej: Administrador">
                    @if (isFieldInvalid('jobTitle')) {
                    <p class="mt-1.5 text-sm text-error-500">{{ getFieldError('jobTitle') }}</p>
                    }
                </div>

                <div>
                    <label for="bio" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Sobre mí
                    </label>
                    <textarea id="bio" formControlName="bio" rows="3" [maxlength]="bioMaxLength"
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:bg-gray-800 dark:text-white transition-colors resize-none"
                        [class.border-error-500]="isFieldInvalid('bio')"
                        placeholder="Cuéntanos un poco sobre ti..."></textarea>
                    <div class="flex justify-between mt-1.5">
                        @if (isFieldInvalid('bio')) {
                        <p class="text-sm text-error-500">{{ getFieldError('bio') }}</p>
                        } @else {
                        <span></span>
                        }
                        <span class="text-xs text-gray-500 dark:text-gray-400">{{ bioLength }}/{{ bioMaxLength }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Zona horaria -->
        <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-6 mb-6">
            <div class="flex items-start justify-between">
                <div>
                    <div class="flex items-center gap-2 mb-1">
                        <svg class="w-5 h-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        <h2 class="text-base font-semibold text-gray-900 dark:text-white">Zona horaria</h2>
                    </div>
                    <p class="text-sm text-gray-600 dark:text-gray-400 mb-5 ml-7">
                        Configura tu zona horaria para mostrar fechas y horas correctamente.
                    </p>
                </div>
                @if (savingTimezone) {
                <svg class="animate-spin h-5 w-5 text-brand-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                }
            </div>

            <div class="space-y-4">
                <div>
                    <label for="timezoneSearch" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Buscar zona horaria
                    </label>
                    <input type="text" id="timezoneSearch" [(ngModel)]="timezoneSearch" [ngModelOptions]="{ standalone: true }"
                        placeholder="Ej: Lima, Bogota, Mexico..."
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:bg-gray-800 dark:text-white transition-colors">
                </div>

                <div>
                    <label for="timezone" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        Zona horaria
                    </label>
                    <select id="timezone" [ngModel]="selectedTimezone" [ngModelOptions]="{ standalone: true }"
                        (ngModelChange)="onTimezoneChange($event)"
                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 dark:bg-gray-800 dark:text-white transition-colors">
                        <option value="">Usar zona horaria de la empresa</option>
                        @for (tz of filteredTimezones; track tz) {
                        <option [value]="tz">{{ tz }}</option>
                        }
                    </select>
                    <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                        @if (selectedTimezone) {
                        Hora actual: {{ timezonePreview }}
                        } @else {
                        Se usa la zona horaria configurada para tu empresa o la de tu navegador.
                        }
                    </p>
                </div>
            </div>
        </div>

        <div class="flex justify-end">
            <app-button type="submit" [disabled]="savingProfile">
                @if (savingProfile) {
                <svg class="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Guardando...
                } @else {
                Guardar cambios
                }
            </app-button>
        </div>
    </form>
</div>
